import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { enumBookingStatus } from '@rematter/frontend-lib-api-client';
import {
  Button,
  EntityDetails,
  Loading,
  LocalizedDate,
  Menu,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { FileText, MoreHorizontal } from 'lucide-react';
import { useLocation, useSearchParams } from 'wouter';

import { BookingStatusBadge } from '../../_components/_shared/StatusBadges';
import { $openApplyAdvancesDialog } from '../../_state/bookingApplyAdvances.state';
import * as state from '../../_state/bookingDetail.state';
import {
  $openBookingInvoiceDialog,
  CreateBookingInvoicesResult,
} from '../../_state/bookingInvoice.state';
import {
  DefaultTranslations,
  useTranslations,
} from '../../i18n/useTranslations';
import { BookingRecord } from '../../types/booking';
import { LoadCreate } from '../loads/LoadCreate';

import { BookingApplyAdvancesDialog } from './BookingApplyAdvancesDialog';
import { BookingInvoiceDialog } from './BookingInvoiceDialog';
import { BookingMarkAsShippedDialog } from './BookingMarkAsShippedDialog';
import BookingTabBasicInfo from './BookingTabBasicInfo';
import { BookingDocumentsTab } from './BookingTabDocuments';
import { BookingTabLoads } from './BookingTabLoads';
import { BookingTabNotes } from './BookingTabNotes';
import { BookingTabPhotos } from './BookingTabPhotos';
import { useBookingDetailViewMenuItems } from './hooks/useBookingDetailViewMenuItems';
import { useRevertBookingStatus } from './hooks/useRevertBookingStatus';
import { VoidBookingDialog } from './VoidBookingDialog';

import { SalesRootLayout } from '~/modules/trade/_components/_layouts/SalesRootLayout';

type BookingDetailViewTab =
  | 'booking-info'
  | 'loads'
  | 'photos'
  | 'documents'
  | 'notes';

const notificationBannerByBookingStatus = ({
  booking,
  t,
}: {
  booking: BookingRecord | null | undefined;
  t: DefaultTranslations;
}) => {
  if (!booking) return null;

  if (booking.status === enumBookingStatus.Draft) {
    return {
      label: (
        <div className="flex flex-row items-center gap-1">
          {t.bookingDetail.notificationDraftBanner()}
        </div>
      ),
      type: 'neutral' as const,
    };
  }

  if (booking.status === enumBookingStatus.Completed) {
    return {
      label: (
        <div className="flex flex-row items-center gap-1">
          {t.bookingDetail.notificationBanner({
            status: (
              <span className="font-bold">
                {t.bookingDetail.statusLowercase.completed}
              </span>
            ),
            date: (
              <span className="font-bold">
                <LocalizedDate date={booking.timestamps.updatedAt} />
              </span>
            ),
          })}
        </div>
      ),
      type: 'success' as const,
    };
  }

  if (booking.status === enumBookingStatus.Voided) {
    return {
      label: (
        <div className="flex flex-row items-center gap-1">
          {t.bookingDetail.notificationBanner({
            status: (
              <span className="font-bold">
                {t.bookingDetail.statusLowercase.voided}
              </span>
            ),
            date: (
              <span className="font-bold">
                <LocalizedDate date={booking.timestamps.updatedAt} />
              </span>
            ),
          })}
        </div>
      ),
      type: 'failure' as const,
    };
  }

  return null;
};

export const BookingDetailView = () => {
  const toast = useToast();
  const t = useTranslations();
  const [, setLocation] = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isLoadDialogOpen, setIsLoadDialogOpen] = React.useState(false);
  const [isVoidDialogOpen, setIsVoidDialogOpen] = React.useState(false);
  const [isShipDialogOpen, setIsShipDialogOpen] = React.useState(false);

  const openInvoiceDialog = useSetAtom($openBookingInvoiceDialog);
  const openApplyAdvancesDialog = useSetAtom($openApplyAdvancesDialog);

  const { data, isLoading, error } = useAtomValue(state.$remoteBookingDetail);

  const booking = data?.booking;

  // Use the menu items hook for contextual actions
  const { items: menuItems } = useBookingDetailViewMenuItems({
    booking: booking ?? null,
    setIsLoadDialogOpen,
    visibility: 'hideIfVoidedOrComplete',
  });

  // Use revert booking status hook at the top level
  const [revertStatus, revertLabelKey] = useRevertBookingStatus({
    status: booking?.status,
    visibility: 'onlyIfVoidedOrComplete',
  });

  // Get active tab from URL params, default to 'booking-info'
  const activeTab = (searchParams.get('tab') ||
    'booking-info') as BookingDetailViewTab;

  // Status transition mutations
  const openBooking = useSetAtom(state.$markAsOpen);
  const voidBooking = useSetAtom(state.$markAsVoided);
  const shipBooking = useSetAtom(state.$markAsShipped);
  const completeBooking = useSetAtom(state.$markAsCompleted);

  // Handler for void booking dialog with load validation
  const handleVoidConfirm = React.useCallback(
    async (bookingId: string, forceUnlink?: boolean) => {
      try {
        const result = await voidBooking({ id: bookingId, forceUnlink });
        const voidResult = result.tradeBookingVoid;

        // Only close dialog and show success if booking was actually voided
        if (voidResult.booking && !voidResult.requiresConfirmation) {
          toast.success(t.bookingDetail.messages.voided);
          setIsVoidDialogOpen(false);
          // Refetch happens automatically via onSuccess in state
        }

        // Return the result to the dialog for handling blocking/unlinkable loads
        return {
          blockingLoads: voidResult.blockingLoads?.map((load) => ({
            id: load.id,
            documentNumber: load.documentNumber,
          })),
          unlinkableLoads: voidResult.unlinkableLoads?.map((load) => ({
            id: load.id,
            documentNumber: load.documentNumber,
          })),
          requiresConfirmation: voidResult.requiresConfirmation,
        };
      } catch (error) {
        toast.error(
          `Failed to void booking: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
        throw error;
      }
    },
    [voidBooking, t, toast],
  );

  const handleShip = React.useCallback(
    async (shippedDate?: CalendarDate) => {
      if (!booking?.id) return;
      try {
        await shipBooking({ id: booking.id, shippedDate });
        toast.success(t.bookingDetail.messages.shipped);
        openInvoiceDialog(booking.id);
      } catch (error) {
        toast.error(
          t.bookingDetail.messages.actionFailed({
            action: 'ship',
            message:
              error instanceof Error
                ? error.message
                : t.bookingDetail.messages.unknownError,
          }),
        );
      }
    },
    [booking?.id, shipBooking, toast, t, openInvoiceDialog],
  );

  const handleComplete = React.useCallback(async () => {
    if (!booking?.id) return;
    try {
      await completeBooking({ id: booking.id });
      toast.success(t.bookingDetail.messages.completed);
    } catch (error) {
      toast.error(
        t.bookingDetail.messages.actionFailed({
          action: 'complete',
          message:
            error instanceof Error
              ? error.message
              : t.bookingDetail.messages.unknownError,
        }),
      );
    }
  }, [booking?.id, completeBooking, toast, t]);

  const handleRevertButton = React.useCallback(async () => {
    if (!booking?.id) return;

    if (booking.status === enumBookingStatus.Completed) {
      await shipBooking({
        id: booking.id,
      });
      toast.success(t.bookingDetail.messages.revertedToShipped);
    } else if (booking.status === enumBookingStatus.Voided) {
      await openBooking({ id: booking.id });
      toast.success(t.bookingDetail.messages.revertedToOpen);
    }
  }, [booking, openBooking, shipBooking, t, toast]);

  const notificationBanner = React.useMemo(
    () => notificationBannerByBookingStatus({ booking, t }),
    [booking, t],
  );

  const handleBack = () => {
    setLocation('/trade/bookings');
  };

  // Render LoadDialog for creating a pending load
  const handleLoadDialogSuccess = React.useCallback(() => {
    setIsLoadDialogOpen(false);
  }, []);

  // Handle successful invoice creation - open advances dialog
  const handleInvoicesCreated = React.useCallback(
    (results: CreateBookingInvoicesResult) => {
      openApplyAdvancesDialog(
        results.map((r) => ({
          accountId: r.accountId,
          accountName: r.accountName,
          invoiceId: r.invoiceId,
          documentNumber: r.documentNumber,
          amount: r.amount,
          currencyCode: r.currencyCode,
        })),
      );
    },
    [openApplyAdvancesDialog],
  );

  const canBeVoided =
    booking?.status !== enumBookingStatus.Voided &&
    booking?.status !== enumBookingStatus.Completed;

  const bookingActions = React.useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Menu
          trigger={
            <Button
              icon={<MoreHorizontal className="size-4" />}
              variant="outline"
              className="w-8"
            />
          }
          items={menuItems}
        />
        {canBeVoided && (
          <Button
            variant="outline"
            color="failure"
            onPress={() => setIsVoidDialogOpen(true)}
          >
            {t.bookingDetail.actions.void}
          </Button>
        )}
        {booking?.status === enumBookingStatus.Open && (
          <BookingMarkAsShippedDialog
            onConfirm={handleShip}
            onClose={() => setIsShipDialogOpen(false)}
            isDialogOpen={isShipDialogOpen}
          >
            <Button
              variant="solid"
              color="primary"
              onPress={() => setIsShipDialogOpen(true)}
            >
              {t.bookingDetail.actions.markAsShipped}
            </Button>
          </BookingMarkAsShippedDialog>
        )}
        {booking?.status === enumBookingStatus.Shipped && (
          <>
            <Button
              variant="outline"
              color="primary"
              onPress={() => openInvoiceDialog(booking.id)}
              icon={<FileText className="size-4" />}
            >
              {t.bookingDetail.actions.generateInvoices}
            </Button>
            <Button variant="solid" color="primary" onPress={handleComplete}>
              {t.bookingDetail.actions.completeBooking}
            </Button>
          </>
        )}
        {revertStatus && revertLabelKey && (
          <Button
            variant="outline"
            color="primary"
            onPress={handleRevertButton}
          >
            {revertLabelKey}
          </Button>
        )}
      </div>
    ),
    [
      menuItems,
      canBeVoided,
      t,
      handleShip,
      handleComplete,
      booking?.id,
      booking?.status,
      openInvoiceDialog,
      revertStatus,
      revertLabelKey,
      handleRevertButton,
    ],
  );

  if (isLoading) {
    return (
      <SalesRootLayout>
        <div className="size-4">
          <Loading />
        </div>
      </SalesRootLayout>
    );
  }

  if (error || !booking) {
    return (
      <SalesRootLayout>
        <div className="size-4 text-failure">
          {t.bookingDetail.error(error?.message || 'Booking not found')}
        </div>
      </SalesRootLayout>
    );
  }

  const isDisabled =
    booking.status === enumBookingStatus.Completed ||
    booking.status === enumBookingStatus.Voided;

  return (
    <SalesRootLayout className="@BookingDetailView">
      <EntityDetails
        onChangeTab={(tab) => setSearchParams({ tab })}
        currentTab={activeTab}
        onBackPressed={handleBack}
        primaryTitle={t.bookingDetail.title(booking.bookingNumber)}
        subtitle={
          <>
            {t.bookingDetail.created}{' '}
            <LocalizedDate date={booking.timestamps.createdAt} />
          </>
        }
        badges={<BookingStatusBadge status={booking.status} />}
        headerActions={bookingActions}
        notificationBanner={notificationBanner}
        tabs={{
          'booking-info': {
            title: t.bookingDetail.tabs.bookingInfo,
            content: () => <BookingTabBasicInfo booking={booking} />,
          },
          loads: {
            title: t.bookingDetail.tabs.loads,
            content: () => (
              <BookingTabLoads booking={booking} disableAddLoad={isDisabled} />
            ),
          },
          photos: {
            title: t.bookingDetail.tabs.photos,
            content: () => <BookingTabPhotos booking={booking} />,
          },
          documents: {
            title: t.bookingDetail.tabs.documents,
            content: () => <BookingDocumentsTab booking={booking} />,
          },
          notes: {
            title: t.bookingDetail.tabs.notes,
            content: () => <BookingTabNotes booking={booking} />,
          },
        }}
      />
      <LoadCreate
        isOpen={isLoadDialogOpen}
        requiredBookingId={booking?.id}
        onClose={() => setIsLoadDialogOpen(false)}
        onSuccess={handleLoadDialogSuccess}
      />
      {isVoidDialogOpen && booking && (
        <VoidBookingDialog
          bookingId={booking.id}
          onVoid={handleVoidConfirm}
          onCancel={() => setIsVoidDialogOpen(false)}
        />
      )}
      <BookingInvoiceDialog onSuccess={handleInvoicesCreated} />
      <BookingApplyAdvancesDialog />
    </SalesRootLayout>
  );
};
